import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type UIEvent as ReactUIEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { pickLocalized, useLang, useT, type Lang } from '../../i18n';
import type {
  MochiVaccineEvent,
  MochiVaccinationsResponse,
  VaccinePlanTrack,
} from '../../types/mochi';

interface PositionedEvent {
  event: MochiVaccineEvent;
  day: number;
  lane: number;
  reminderDay: number | null;
}

interface TooltipPosition {
  left: number;
  top: number;
}

const WIDTH = 1540;
const AXIS_X = 136;
const MILESTONES_LEFT = 212;
const LANE_COUNT = 6;
const LANE_WIDTH = 160;
const MILESTONE_LABEL_HEIGHT = 34;
const PLOT_TOP = 92;
const PLOT_BOTTOM = 120;
const PLAN_YEARS = 2;
const DAY_HEIGHT = 6.4;
const APPOINTMENT_REMINDER_DAYS = 13;

const trackOrder: VaccinePlanTrack[] = ['national', 'recommended', 'optional'];
const trackStyles: Record<VaccinePlanTrack, { fill: string; stroke: string; text: string; dot: string }> = {
  national: { fill: '#e0f2fe', stroke: '#0284c7', text: '#075985', dot: 'bg-sky-600' },
  recommended: { fill: '#ffedd5', stroke: '#f97316', text: '#9a3412', dot: 'bg-orange-500' },
  optional: { fill: '#f3e8ff', stroke: '#9333ea', text: '#6b21a8', dot: 'bg-purple-600' },
};

export default function MochiVaccineTimeline({ plan }: { plan: MochiVaccinationsResponse }) {
  const t = useT();
  const { lang } = useLang();
  const scrollRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const birth = useMemo(() => parseLocalDate(plan.birth_date), [plan.birth_date]);
  const planEnd = useMemo(() => addCalendarYears(birth, PLAN_YEARS), [birth]);
  const planEndDay = dayDiff(birth, planEnd);
  const todayDay = clamp(dayDiff(birth, startOfDay(new Date())), 0, Number.MAX_SAFE_INTEGER);
  const positioned = useMemo(
    () => layoutEvents(plan.events, birth).filter(({ day }) => day >= 0 && day <= planEndDay),
    [plan.events, birth, planEndDay],
  );
  const lastDay = planEndDay;
  const chartHeight = Math.ceil(PLOT_TOP + lastDay * DAY_HEIGHT + PLOT_BOTTOM);
  const [selectedDay, setSelectedDay] = useState(Math.min(todayDay, lastDay));
  const [enabled, setEnabled] = useState<Record<VaccinePlanTrack, boolean>>({
    national: true,
    recommended: true,
    optional: true,
  });
  const [tooltip, setTooltip] = useState<TooltipPosition | null>(null);
  const didInitialScroll = useRef(false);

  const visibleEvents = positioned.filter(({ event }) => enabled[event.track]);
  const selectedEvents = visibleEvents.filter(({ day }) => day === selectedDay).map(({ event }) => event);
  const selectedReminders = visibleEvents
    .filter(({ reminderDay }) => reminderDay === selectedDay)
    .map(({ event }) => event);
  const selectedDate = addDays(birth, selectedDay);
  const selectedSummaries = [
    ...selectedEvents.map((event) => `${pickLocalized(event.short_name, lang)} ${pickLocalized(event.dose, lang)}`),
    ...selectedReminders.map((event) => t('mochi.vaccine.reminderSummary', {
      vaccine: pickLocalized(event.short_name, lang),
      dose: pickLocalized(event.dose, lang),
    })),
  ];
  const rv5Schedule = plan.events
    .filter((event) => event.vaccine_code === 'rotavirus')
    .map((event) => `${pickLocalized(event.dose, lang)} ${event.target_date}`)
    .join(' · ');
  const completedCount = positioned.filter(({ event }) => event.status === 'completed').length;
  const nextDay = positioned
    .filter(({ event, day }) => event.status === 'planned' && day >= todayDay)
    .reduce<number | null>((nearest, item) => nearest === null || item.day < nearest ? item.day : nearest, null);
  const nextEvents = nextDay === null
    ? []
    : positioned.filter(({ event, day }) => day === nextDay && event.status === 'planned');
  const ticks = buildTicks(birth, lastDay);

  useEffect(() => {
    if (didInitialScroll.current) return;
    const container = scrollRef.current;
    if (!container) return;
    didInitialScroll.current = true;
    window.requestAnimationFrame(() => {
      const target = PLOT_TOP + Math.min(todayDay, lastDay) * DAY_HEIGHT - container.clientHeight * 0.32;
      container.scrollTop = clamp(target, 0, Math.max(0, container.scrollHeight - container.clientHeight));
    });
  }, [lastDay, todayDay]);

  function selectFromScroll(event: ReactUIEvent<HTMLDivElement>) {
    const container = event.currentTarget;
    const focusY = container.scrollTop + container.clientHeight * 0.32;
    setSelectedDay(clamp(Math.round((focusY - PLOT_TOP) / DAY_HEIGHT), 0, lastDay));
    setTooltip(null);
  }

  function selectFromPointer(event: ReactPointerEvent<SVGSVGElement>) {
    const svg = svgRef.current;
    if (!svg) return;
    const bounds = svg.getBoundingClientRect();
    const renderedY = ((event.clientY - bounds.top) / bounds.height) * chartHeight;
    setSelectedDay(clamp(Math.round((renderedY - PLOT_TOP) / DAY_HEIGHT), 0, lastDay));
    if (event.pointerType === 'mouse') {
      const panelWidth = Math.min(320, window.innerWidth - 16);
      const roomOnRight = event.clientX + panelWidth + 18 <= window.innerWidth;
      setTooltip({
        left: Math.max(8, roomOnRight ? event.clientX + 14 : event.clientX - panelWidth - 14),
        top: clamp(event.clientY - 54, 8, Math.max(8, window.innerHeight - 420)),
      });
    }
  }

  function jumpToDay(day: number) {
    const container = scrollRef.current;
    setSelectedDay(clamp(day, 0, lastDay));
    if (!container) return;
    const target = PLOT_TOP + day * DAY_HEIGHT - container.clientHeight * 0.32;
    container.scrollTo({ top: clamp(target, 0, container.scrollHeight - container.clientHeight), behavior: 'smooth' });
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <SummaryCard label={t('mochi.vaccine.completed')} value={t('mochi.vaccine.completedCount', { count: completedCount })} />
        <SummaryCard
          label={t('mochi.vaccine.next')}
          value={nextDay === null ? t('mochi.vaccine.noNext') : formatIsoDate(addDays(birth, nextDay))}
          detail={nextEvents.map(({ event }) => `${pickLocalized(event.short_name, lang)} ${pickLocalized(event.dose, lang)}`).join(' · ')}
        />
        <SummaryCard label={t('mochi.vaccine.updated')} value={plan.updated_at} detail={t('mochi.vaccine.clinicRule')} />
      </div>

      <div className="rounded-xl border border-orange-200 bg-orange-50/70 p-4 text-sm leading-relaxed text-coffee">
        <strong className="text-cocoa">{t('mochi.vaccine.routeTitle')}</strong>{' '}
        {t('mochi.vaccine.routeCopy')}
        {rv5Schedule && (
          <p className="mt-2 font-mono text-xs text-cocoa/80">
            <strong>{t('mochi.vaccine.rv5Schedule')}</strong>{' '}{rv5Schedule}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-4 border-y border-neutral-200 py-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-coffee/70">{t('mochi.vaccine.selected')}</p>
          <p className="mt-1 text-2xl font-semibold text-cocoa">{formatIsoDate(selectedDate)}</p>
          <p className="mt-1 font-mono text-xs text-coffee">
            {t('mochi.vaccine.daySinceBirth', { day: selectedDay })} · {formatBabyAge(birth, selectedDate, lang)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <JumpButton onClick={() => jumpToDay(0)}>{t('mochi.vaccine.jumpBirth')}</JumpButton>
          <JumpButton onClick={() => jumpToDay(Math.min(todayDay, lastDay))}>{t('mochi.vaccine.jumpToday')}</JumpButton>
          {nextDay !== null && <JumpButton onClick={() => jumpToDay(nextDay)}>{t('mochi.vaccine.jumpNext')}</JumpButton>}
        </div>
      </div>

      <fieldset>
        <legend className="font-mono text-xs uppercase tracking-[0.2em] text-coffee/70">{t('mochi.vaccine.filters')}</legend>
        <div className="mt-3 flex flex-wrap gap-2">
          {trackOrder.map((track) => (
            <label key={track} className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-neutral-200 bg-white px-3 py-2 font-mono text-xs text-coffee shadow-sm">
              <input
                type="checkbox"
                checked={enabled[track]}
                onChange={() => setEnabled((current) => ({ ...current, [track]: !current[track] }))}
                className="accent-orange-500"
              />
              <span className={`h-2.5 w-2.5 rotate-45 rounded-[1px] ${trackStyles[track].dot}`} />
              {t(`mochi.vaccine.tracks.${track}`)}
            </label>
          ))}
        </div>
      </fieldset>

      <div>
        <h2 className="text-xl font-semibold text-cocoa">{t('mochi.vaccine.chartTitle')}</h2>
        <p className="mt-1 font-mono text-xs text-coffee/70">
          {t('mochi.vaccine.planWindow', { start: formatIsoDate(birth), end: formatIsoDate(planEnd) })}
        </p>
        <p className="mt-3 font-mono text-xs leading-relaxed text-coffee/70">{t('mochi.vaccine.scrollHint')}</p>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-xs text-coffee" aria-label={t('mochi.vaccine.markerLegend')}>
          <span className="inline-block h-3 w-3 rotate-45 border-2 border-orange-500 bg-white" aria-hidden="true" />
          <span>{t('mochi.vaccine.appointmentReminder')}</span>
          <span className="h-px w-6 bg-orange-400" aria-hidden="true" />
          <span className="inline-block h-3 w-3 rotate-45 bg-orange-500" aria-hidden="true" />
          <span>{t('mochi.vaccine.vaccinationDay')}</span>
        </div>
      </div>

      <div className="relative">
        <div className="pointer-events-none absolute left-2 top-2 z-10 max-w-[calc(100%-1rem)] rounded-lg border border-neutral-700/70 bg-neutral-950/90 px-3 py-2 text-neutral-100 shadow-card backdrop-blur">
          <p className="font-mono text-[11px] text-neutral-300">{formatIsoDate(selectedDate)} · {t('mochi.vaccine.daySinceBirth', { day: selectedDay })}</p>
          {selectedSummaries.length > 0 && (
            <p className="mt-1 max-w-[20rem] text-xs leading-relaxed text-white">
              {selectedSummaries.join(' · ')}
            </p>
          )}
        </div>
        <div
          ref={scrollRef}
          onScroll={selectFromScroll}
          className="max-h-[78vh] overflow-auto rounded-xl border border-neutral-200 bg-white/55 shadow-soft"
        >
          <svg
          ref={svgRef}
          width={WIDTH}
          height={chartHeight}
          role="img"
          aria-label={t('mochi.vaccine.chartAria')}
          className="block max-w-none cursor-crosshair select-none"
          onPointerMove={selectFromPointer}
          onPointerEnter={selectFromPointer}
          onPointerLeave={() => setTooltip(null)}
          >
          <title>{t('mochi.vaccine.chartTitle')}</title>
          <desc>{t('mochi.vaccine.chartAria')}</desc>
          <rect width={WIDTH} height={chartHeight} fill="transparent" />

          {ticks.map((tick) => {
            const y = PLOT_TOP + tick.day * DAY_HEIGHT;
            return (
              <g key={`${tick.day}-${tick.label}`}>
                <line x1={AXIS_X} x2={WIDTH - 24} y1={y} y2={y} stroke={tick.major ? '#d4d4d4' : '#e5e5e5'} strokeDasharray={tick.major ? undefined : '2 7'} />
                <line x1={AXIS_X - 7} x2={AXIS_X + 7} y1={y} y2={y} stroke="#737373" />
                <text x={AXIS_X - 14} y={y - 3} textAnchor="end" fill="#404040" className="font-mono text-[12px]">{tick.label}</text>
                <text x={AXIS_X - 14} y={y + 13} textAnchor="end" fill="#737373" className="font-mono text-[10px]">
                  {tick.week === null ? `D+${tick.day}` : `W+${tick.week} · D+${tick.day}`}
                </text>
              </g>
            );
          })}

          <line x1={AXIS_X} x2={AXIS_X} y1={PLOT_TOP} y2={chartHeight - PLOT_BOTTOM / 2} stroke="#525252" strokeWidth="2" />

          {visibleEvents.map(({ event, day, lane, reminderDay }) => {
            const y = PLOT_TOP + day * DAY_HEIGHT;
            const reminderY = reminderDay === null ? null : PLOT_TOP + reminderDay * DAY_HEIGHT;
            const x = MILESTONES_LEFT + lane * LANE_WIDTH;
            const style = event.status === 'completed'
              ? { fill: '#dcfce7', stroke: '#16a34a', text: '#166534' }
              : trackStyles[event.track];
            const shortName = pickLocalized(event.short_name, lang);
            const dose = pickLocalized(event.dose, lang);
            const diamondRadius = event.status === 'completed' ? 8 : 6.5;
            const textHalo = {
              paintOrder: 'stroke' as const,
              stroke: '#ffffff',
              strokeWidth: 5,
              strokeLinecap: 'round' as const,
              strokeLinejoin: 'round' as const,
            };
            return (
              <g key={event.event_id} aria-label={`${shortName} ${dose}`}>
                <line x1={AXIS_X} x2={x - diamondRadius - 4} y1={y} y2={y} stroke={style.stroke} strokeOpacity="0.38" />
                {reminderDay !== null && reminderY !== null && (
                  <>
                    <line
                      x1={AXIS_X}
                      x2={x - diamondRadius - 4}
                      y1={reminderY}
                      y2={reminderY}
                      stroke={style.stroke}
                      strokeDasharray="3 5"
                      strokeOpacity="0.2"
                    />
                    <line
                      x1={x}
                      x2={x}
                      y1={reminderY + diamondRadius + 3}
                      y2={y - diamondRadius - 3}
                      stroke={style.stroke}
                      strokeWidth="1.5"
                      strokeOpacity="0.72"
                    />
                    <rect
                      x={x - diamondRadius}
                      y={reminderY - diamondRadius}
                      width={diamondRadius * 2}
                      height={diamondRadius * 2}
                      transform={`rotate(45 ${x} ${reminderY})`}
                      fill="#ffffff"
                      stroke={style.stroke}
                      strokeWidth="2"
                    />
                    <text x={x + 15} y={reminderY + 4} fill={style.text} style={textHalo} opacity="0.82" className="font-mono text-[10px] font-medium">
                      {t('mochi.vaccine.reminderMarker', { date: formatMonthDay(addDays(birth, reminderDay)) })}
                    </text>
                  </>
                )}
                <rect
                  x={AXIS_X - 3.5}
                  y={y - 3.5}
                  width="7"
                  height="7"
                  transform={`rotate(45 ${AXIS_X} ${y})`}
                  fill={style.stroke}
                  stroke="white"
                  strokeWidth="1.5"
                />
                <rect
                  x={x - diamondRadius}
                  y={y - diamondRadius}
                  width={diamondRadius * 2}
                  height={diamondRadius * 2}
                  transform={`rotate(45 ${x} ${y})`}
                  fill={style.stroke}
                  stroke={style.stroke}
                  strokeWidth={event.status === 'completed' ? 2.5 : 2}
                />
                <text x={x + 15} y={y - 3} fill={style.text} style={textHalo} className="font-mono text-[12px] font-semibold">
                  {truncateLabel(shortName, lang === 'zh' ? 12 : 19)}
                </text>
                <text x={x + 15} y={y + 13} fill={style.text} style={textHalo} opacity="0.82" className="font-mono text-[10px]">
                  {truncateLabel(`${dose}${event.status === 'completed' ? ` · ${t('mochi.vaccine.done')}` : ''}`, lang === 'zh' ? 18 : 25)}
                </text>
              </g>
            );
          })}

          {todayDay <= lastDay && (
            <g>
              <line x1={AXIS_X - 16} x2={WIDTH - 24} y1={PLOT_TOP + todayDay * DAY_HEIGHT} y2={PLOT_TOP + todayDay * DAY_HEIGHT} stroke="#0a0a0a" strokeDasharray="4 7" strokeWidth="1.5" />
              <text x={WIDTH - 28} y={PLOT_TOP + todayDay * DAY_HEIGHT - 7} textAnchor="end" fill="#0a0a0a" className="font-mono text-[11px]">{t('mochi.vaccine.today')}</text>
            </g>
          )}

          <line
            x1={AXIS_X - 16}
            x2={WIDTH - 24}
            y1={PLOT_TOP + selectedDay * DAY_HEIGHT}
            y2={PLOT_TOP + selectedDay * DAY_HEIGHT}
            stroke="#f97316"
            strokeWidth="2"
            className="pointer-events-none"
          />
          </svg>
        </div>
      </div>

      {tooltip && createPortal(
        <VaccineTooltip
          position={tooltip}
          date={selectedDate}
          day={selectedDay}
          age={formatBabyAge(birth, selectedDate, lang)}
          events={selectedEvents}
          reminders={selectedReminders}
          lang={lang}
        />,
        document.body,
      )}

      <div className="grid gap-4 border-t border-neutral-200 pt-5 text-sm leading-relaxed text-coffee lg:grid-cols-2">
        <div>
          <h2 className="font-semibold text-cocoa">{t('mochi.vaccine.howToReadTitle')}</h2>
          <p className="mt-2">{t('mochi.vaccine.howToRead')}</p>
        </div>
        <div>
          <h2 className="font-semibold text-cocoa">{t('mochi.vaccine.specialTitle')}</h2>
          <p className="mt-2">{t('mochi.vaccine.specialCopy')}</p>
        </div>
      </div>

      <div className="space-y-2 border-t border-neutral-200 pt-5 font-mono text-xs leading-relaxed text-coffee/75">
        <p>{t('mochi.vaccine.sourceNote')}</p>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          <a className="border-b border-neutral-400 hover:border-cocoa hover:text-cocoa" href="https://www.ndcpa.gov.cn/jbkzzx/c100013/common/content/content_2073002930292035584.html" target="_blank" rel="noreferrer">{t('mochi.vaccine.sourceNational')}</a>
          <a className="border-b border-neutral-400 hover:border-cocoa hover:text-cocoa" href="https://www.ndcpa.gov.cn/jbkzzx/c100040/common/content/content_1877524874852421632.html" target="_blank" rel="noreferrer">{t('mochi.vaccine.sourceSubstitution')}</a>
          <a className="border-b border-neutral-400 hover:border-cocoa hover:text-cocoa" href="https://www.chinacdc.cn/jkyj/mygh02/ymkyfjb/jshzy/202606/t20260618_1837073.html" target="_blank" rel="noreferrer">{t('mochi.vaccine.sourcePolio')}</a>
          <a className="border-b border-neutral-400 hover:border-cocoa hover:text-cocoa" href="https://zjjcmspublic.oss-cn-hangzhou-zwynet-d01-a.internet.cloud.zj.gov.cn/jcms_files/jcms1/web2241/site/attach/0/989dc1efd34643ac9bfe74e7ff56e4fd.pdf" target="_blank" rel="noreferrer">{t('mochi.vaccine.sourceZhejiang')}</a>
          <a className="border-b border-neutral-400 hover:border-cocoa hover:text-cocoa" href="https://www.sanofi.cn/assets/dot-cn/pages/docs/your-health/vaccine-publications/repackaging-pentaxim-20250816.pdf" target="_blank" rel="noreferrer">{t('mochi.vaccine.sourcePentaxim')}</a>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white/75 p-4 shadow-soft">
      <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-coffee/65">{label}</p>
      <p className="mt-2 text-xl font-semibold text-cocoa">{value}</p>
      {detail && <p className="mt-2 text-xs leading-relaxed text-coffee">{detail}</p>}
    </div>
  );
}

function JumpButton({ children, onClick }: { children: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="rounded-md border border-neutral-300 bg-white px-3 py-2 font-mono text-xs text-coffee shadow-sm transition hover:border-orange-400 hover:text-cocoa">
      {children}
    </button>
  );
}

function VaccineTooltip({
  position,
  date,
  day,
  age,
  events,
  reminders,
  lang,
}: {
  position: TooltipPosition;
  date: Date;
  day: number;
  age: string;
  events: MochiVaccineEvent[];
  reminders: MochiVaccineEvent[];
  lang: Lang;
}) {
  const t = useT();
  return (
    <div
      role="tooltip"
      className="pointer-events-none fixed z-50 max-h-[calc(100vh-1rem)] w-[min(20rem,calc(100vw-1rem))] overflow-y-auto rounded-xl border border-neutral-700 bg-neutral-950 p-4 text-neutral-100 shadow-float"
      style={position}
    >
      <p className="text-base font-semibold">{formatIsoDate(date)}</p>
      <p className="mt-1 font-mono text-xs text-neutral-300">{t('mochi.vaccine.daySinceBirth', { day })} · {age}</p>
      {events.length === 0 && reminders.length === 0 ? (
        <p className="mt-3 border-t border-neutral-700 pt-3 text-xs text-neutral-400">{t('mochi.vaccine.noEvent')}</p>
      ) : (
        <div className="mt-3 space-y-4 border-t border-neutral-700 pt-3">
          {reminders.map((event) => (
            <div key={`reminder-${event.event_id}`} className="space-y-1.5 rounded-lg border border-neutral-700 bg-neutral-900/80 p-3 text-xs leading-relaxed">
              <p className="font-mono text-orange-300">{t('mochi.vaccine.appointmentReminder')}</p>
              <strong className="block text-sm font-medium text-white">
                {pickLocalized(event.full_name, lang)} · {pickLocalized(event.dose, lang)}
              </strong>
              <p className="text-neutral-300">{t('mochi.vaccine.reminderForDate', { date: event.target_date })}</p>
            </div>
          ))}
          {events.map((event) => (
            <div key={event.event_id} className="space-y-1.5 text-xs leading-relaxed">
              <div className="flex items-start justify-between gap-3">
                <strong className="text-sm font-medium text-white">{pickLocalized(event.full_name, lang)}</strong>
                <span className="shrink-0 rounded bg-neutral-800 px-2 py-0.5 font-mono text-[10px] text-neutral-200">{pickLocalized(event.dose, lang)}</span>
              </div>
              <p className="font-mono text-neutral-300">
                {t(`mochi.vaccine.tracks.${event.track}`)} · {event.funding === 'free' ? t('mochi.vaccine.free') : formatPrice(event.price_cny, t)} · {t(`mochi.vaccine.origins.${event.origin}`)}
              </p>
              {event.administered_date && event.administered_date !== event.target_date && (
                <p className="font-mono text-emerald-300">{t('mochi.vaccine.targetVsActual', { target: event.target_date, actual: event.administered_date })}</p>
              )}
              <p className="text-neutral-300">{pickLocalized(event.note, lang)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function layoutEvents(events: MochiVaccineEvent[], birth: Date): PositionedEvent[] {
  const laneEnds = Array.from({ length: LANE_COUNT }, () => -Infinity);
  return [...events]
    .map((event) => {
      const day = dayDiff(birth, parseLocalDate(event.administered_date ?? event.target_date));
      const reminderDay = event.status === 'planned' && day >= APPOINTMENT_REMINDER_DAYS
        ? day - APPOINTMENT_REMINDER_DAYS
        : null;
      return { event, day, reminderDay };
    })
    .sort((left, right) => (left.reminderDay ?? left.day) - (right.reminderDay ?? right.day)
      || left.event.event_id.localeCompare(right.event.event_id))
    .map(({ event, day, reminderDay }) => {
      const startY = PLOT_TOP + (reminderDay ?? day) * DAY_HEIGHT;
      const endY = PLOT_TOP + day * DAY_HEIGHT + MILESTONE_LABEL_HEIGHT / 2;
      let lane = laneEnds.findIndex((laneEndY) => startY - MILESTONE_LABEL_HEIGHT / 2 > laneEndY + 8);
      if (lane < 0) lane = laneEnds.indexOf(Math.min(...laneEnds));
      laneEnds[lane] = endY;
      return { event, day, lane, reminderDay };
    });
}

function buildTicks(birth: Date, lastDay: number): Array<{ day: number; label: string; major: boolean; week: number | null }> {
  const ticks: Array<{ day: number; label: string; major: boolean; week: number | null }> = [];
  for (let week = 0; week * 7 <= lastDay; week += 1) {
    const day = week * 7;
    ticks.push({
      day,
      label: formatIsoDate(addDays(birth, day)),
      major: week % 4 === 0,
      week,
    });
  }
  if (ticks.at(-1)?.day !== lastDay) {
    ticks.push({
      day: lastDay,
      label: formatIsoDate(addDays(birth, lastDay)),
      major: true,
      week: null,
    });
  }
  return ticks;
}

function formatPrice(price: { min: number; max: number } | undefined, t: ReturnType<typeof useT>): string {
  if (!price) return t('mochi.vaccine.priceVaries');
  if (price.min === price.max) return t('mochi.vaccine.priceOne', { price: price.min });
  return t('mochi.vaccine.priceRange', { min: price.min, max: price.max });
}

function parseLocalDate(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function addCalendarMonths(date: Date, months: number): Date {
  const first = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  return new Date(first.getFullYear(), first.getMonth(), Math.min(date.getDate(), lastDay));
}

function addCalendarYears(date: Date, years: number): Date {
  const targetYear = date.getFullYear() + years;
  const lastDay = new Date(targetYear, date.getMonth() + 1, 0).getDate();
  return new Date(targetYear, date.getMonth(), Math.min(date.getDate(), lastDay));
}

function dayDiff(start: Date, end: Date): number {
  return Math.round((Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()) - Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())) / 86_400_000);
}

function formatIsoDate(date: Date): string {
  return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-');
}

function formatMonthDay(date: Date): string {
  return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatBabyAge(birth: Date, date: Date, lang: Lang): string {
  let years = date.getFullYear() - birth.getFullYear();
  let yearAnchor = addCalendarYears(birth, years);
  if (yearAnchor > date) {
    years -= 1;
    yearAnchor = addCalendarYears(birth, years);
  }
  let months = (date.getFullYear() - yearAnchor.getFullYear()) * 12 + date.getMonth() - yearAnchor.getMonth();
  let monthAnchor = addCalendarMonths(yearAnchor, months);
  if (monthAnchor > date) {
    months -= 1;
    monthAnchor = addCalendarMonths(yearAnchor, months);
  }
  const days = dayDiff(monthAnchor, date);
  if (lang === 'zh') return years > 0 ? `${years}岁${months}个月${days}天` : `${months}个月${days}天`;
  const parts = [];
  if (years > 0) parts.push(`${years} ${years === 1 ? 'year' : 'years'}`);
  parts.push(`${months} ${months === 1 ? 'month' : 'months'}`);
  parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  return parts.join(' ');
}

function truncateLabel(value: string, length: number): string {
  return value.length > length ? `${value.slice(0, length - 1)}…` : value;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
